import React, { useState, useEffect } from 'react';

import TableHeader from '@/components/TableHeader';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import useMediaQuery from '@/hooks/useMediaQuery';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import Chip from '@/components/Chip';
import Link from '@/elements/Link';
import IconicButton from '@/components/IconicButton';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';

const HEADER_FIELDS = [
  { name: 'WORKER', style: { width: '18%', color: 'black' } },
  { name: 'TITLE', style: { width: '18%', color: 'black' } },
  { name: 'HELLOSIGN ID', style: { width: '14%', color: 'black' } },
  { name: 'TYPE', style: { width: '10%', color: 'black' } },
  { name: 'STATUS', style: { width: '10%', color: 'black' } },
  { name: 'MIGRATION', style: { width: '10%', color: 'black' } },
  { name: 'MIGRATED AT', style: { width: '12%', color: 'black' } },
  { name: 'DOWNLOAD', style: { width: '8%', color: 'black' } },
];

type HelloSignDocument = {
  id: number;
  worker_id: number;
  worker_name: string;
  hellosign_id: string;
  request_type: string;
  request_status: string;
  title: string;
  migration_status: string;
  s3_key: string | null;
  file_name: string | null;
  content_type: string | null;
  byte_size: number | null;
  checksum_sha256: string | null;
  migrated_at: string | null;
  source_completed_at: string | null;
  last_error: string | null;
  created_at: string;
  downloadable: boolean;
};

type PaginationData = {
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return dateString;
  }
};

const getMigrationChip = (status: string) => {
  switch (status) {
    case 'migrated':
      return <Chip color="success" label="Migrated" size="small" />;
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    case 'failed':
      return <Chip color="error" label="Failed" size="small" />;
    case 'skipped':
      return <Chip label="Skipped" size="small" />;
    default:
      return <Chip label={status || 'Unknown'} size="small" />;
  }
};

const getStatusChip = (status: string) => {
  switch (status) {
    case 'completed':
      return <Chip color="success" label="Completed" size="small" />;
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    default:
      return (
        <Chip
          style={{ textTransform: 'capitalize' }}
          label={status || '-'}
          size="small"
        />
      );
  }
};

const HelloSignAudit: React.FC = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<HelloSignDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({
    total_count: 0,
    current_page: 1,
    per_page: 25,
    total_pages: 0,
  });
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== query) {
        setQuery(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        fetchDocuments(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, query]);

  useEffect(() => {
    fetchDocuments(query, 1);
  }, [statusFilter]);

  const fetchDocuments = async (searchQuery?: string, page: number = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page,
        per_page: pagination.per_page,
      };
      const search = searchQuery ?? query;
      if (search) params.search = search;
      if (statusFilter) params.migration_status = statusFilter;

      const response = await axiosRubyClient.get(
        '/api/admin/hellosign_document_audit',
        { params },
      );
      setDocuments(response.data.data);

      if (response.data.pagination) {
        setPagination({
          total_count: response.data.pagination.total_count,
          current_page: response.data.pagination.current_page,
          per_page: response.data.pagination.per_page || pagination.per_page,
          total_pages: Math.ceil(
            response.data.pagination.total_count /
              (response.data.pagination.per_page || pagination.per_page),
          ),
        });
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching HelloSign documents:', err);
      setError('Error loading HelloSign documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: HelloSignDocument) => {
    if (!doc.downloadable) return;

    setDownloading(doc.id);
    try {
      const response = await axiosRubyClient.get(
        `/api/admin/hellosign_document_audit/${doc.id}/download`,
      );

      if (response.data.success && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const onQueryChange = (value: string) => {
    setSearchInput(value);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchDocuments(query, page);
  };

  const onStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  return (
    <Page
      size="full"
      title="HelloSign Document Audit"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchDocuments(query, pagination.current_page),
      }}
    >
      <Card>
        <Card.Section>
          <Stack gap={phoneOnly ? 16 : 8} vertical={phoneOnly}>
            <div style={{ flex: 1 }}>
              <TextInput
                id="input-search-hellosign"
                placeholder="Search by worker name or HelloSign ID..."
                value={searchInput}
                onChange={(ev) => onQueryChange(ev.currentTarget.value)}
              />
            </div>
            <div style={{ minWidth: '180px' }}>
              <select
                value={statusFilter}
                onChange={(ev) => onStatusFilterChange(ev.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  height: '40px',
                }}
              >
                <option value="">All statuses</option>
                <option value="migrated">Migrated</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
          </Stack>
        </Card.Section>

        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading && documents.length > 0 ? (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <TextStack spacing="tight">
                      <Link to={`/workers/${doc.worker_id}`}>
                        <Body color="theme" weight="medium">
                          {doc.worker_name}
                        </Body>
                      </Link>
                      <Body size="sm" color="light">
                        ID: {doc.worker_id}
                      </Body>
                    </TextStack>
                  </TableCell>
                  <TableCell>
                    <Body>{doc.title || '-'}</Body>
                  </TableCell>
                  <TableCell>
                    <Body size="sm" style={{ wordBreak: 'break-all' }}>
                      {doc.hellosign_id}
                    </Body>
                  </TableCell>
                  <TableCell>
                    <Body style={{ textTransform: 'capitalize' }}>
                      {doc.request_type?.replace(/_/g, ' ') || '-'}
                    </Body>
                  </TableCell>
                  <TableCell>{getStatusChip(doc.request_status)}</TableCell>
                  <TableCell>
                    <Stack vertical gap={4}>
                      {getMigrationChip(doc.migration_status)}
                      {doc.last_error && (
                        <Body
                          size="sm"
                          color="error"
                          title={doc.last_error}
                          style={{
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.last_error}
                        </Body>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Body size="sm">{formatDate(doc.migrated_at)}</Body>
                  </TableCell>
                  <TableCell>
                    {doc.downloadable ? (
                      <IconicButton
                        a11yLabel="Download document"
                        appearance="primary"
                        icon={faDownload}
                        size="sm"
                        disabled={downloading === doc.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(doc);
                        }}
                      />
                    ) : (
                      <Body size="sm" color="light">
                        -
                      </Body>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : !loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADER_FIELDS.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <Body>No HelloSign documents found.</Body>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {loading && (
          <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
            <LoadingState
              overlayColor="white"
              text="Loading HelloSign documents"
            />
          </Stack>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            <Body>{error}</Body>
          </div>
        )}

        {!loading && documents.length > 0 && pagination.total_pages > 1 && (
          <Card.Section>
            <Stack justify="center">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                onPageChange={onPageChange}
              />
            </Stack>
          </Card.Section>
        )}
      </Card>
    </Page>
  );
};

export default HelloSignAudit;
